import {Box, Button, SvgIcon, Typography} from "@mui/material";
import {HowToRegOutlined, PersonAddOutlined} from "@mui/icons-material";
import React, {ReactElement, useEffect} from "react";
import {StyledBoxColumn, StyledBoxRow, StyledListItem, StyledSpan} from "@/shared/StyledComponents";
import {BaseApp} from "@/shared/BaseApp";
import {ClaimCodeInput} from "./ClaimCodeInput";
import {DW_LG, PAGE_FRAGMENT_WIDTH, PD_LG, PD_MD} from "@/shared/dimens";
import {
  AccountTypeName,
  Page,
  PageClaim,
  PageClaims,
  Pages,
  PROVIDER_PAGE_PROFILE_ROLES_KTM,
  PROVIDER_PAGE_PROFILE_SUPPORTED_ROLES,
  ProviderPageProfile,
  ProviderPageProfileRole,
  QuickOnboardingStep
} from "@/libwyze-shared/types";
import {FormGenMultiStepHelper} from "@/shared/FormGenMultiStepHelper";
import {Account, AccountContextTrait, Action} from "@/shared/types";
import {QuickOnboardingStepCompleted} from "./types";
import {appTypeObject} from "@/libwyze-common/types";

export class SetupProviderPageProfileHelper {

  static setup(): void {
    if (QuickOnboardingStepCompleted(QuickOnboardingStep.QUICK_SETUP_PROVIDER)) {
      return;
    }
    const account = BaseApp.CONTEXT.getTrait<AccountContextTrait>().getAccountByType(AccountTypeName.PROVIDER);
    const id = BaseApp.CONTEXT.showDialog(null, () => <SetupProviderPageProfileView
      onProfileActionClicked={(action: UnlinkedProfileAction) => {
        BaseApp.CONTEXT.hideDialog(id);
        setTimeout(() => {
          switch (action.id) {
            case "claim": {
              BaseApp.CONTEXT.showCustomDialog("Claim an Exising Profile", <ClaimCodeInput
                onVerify={async (code) => {
                  BaseApp.CONTEXT.hideDialog();
                  const page = await Pages.getInstance().loadPageByClaimCode(code);
                  if (!page) {
                    return false;
                  }
                  await PageClaims.getInstance().addPageClaim(PageClaim.createNewEditor(page.id, page.type, account.id));
                  window.location.reload();
                  return true;
                }}/>, null, null, null, DW_LG);
              break;
            }
            case "create": {
              const id = BaseApp.CONTEXT.showDialog(null, () => <ProviderPageProfileRolesView
                onConfirmClicked={(roles: ProviderPageProfileRole[]) => {
                  BaseApp.CONTEXT.hideDialog(id);
                  setTimeout(() => this.showCreateWizard(account, roles), 100);
                }}/>);
              break;
            }
          }
        }, 100);
      }}
    />);
  }

  static editRoles(page: Page): void {
    if (!page.providerPageProfile) {
      return;
    }
    const id = BaseApp.CONTEXT.showDialog(null, () => <ProviderPageProfileRolesView
      selectedRoles={page.providerPageProfile.roles}
      onConfirmClicked={(roles: ProviderPageProfileRole[]) => {
        BaseApp.CONTEXT.hideDialog(id);
        page.providerPageProfile.roles = roles;
        Pages.getInstance().addPage(page)
          .then(() => window.location.reload());
      }}/>);
  }

  private static showCreateWizard(account: Account, roles: ProviderPageProfileRole[]) {
    const page = Page.createNewProviderPage(account, roles);
    FormGenMultiStepHelper.showDialog(page.providerPageProfile, {
      title: "New profile",
      finishAction: new Action("Finish", () => {
        BaseApp.CONTEXT.showProgressDialog("Please wait...", null, async function () {
          await Pages.getInstance().addPage(page);
          await PageClaims.getInstance().addPageClaim(PageClaim.createNewEditor(page.id, page.type, account.id));
          window.location.reload();
        });
      }),
      steps: ProviderPageProfile.multiStepConfigSteps(true),
    }, true);
  }
}

type UnlinkedProfileAction = {
  id: string,
  title: string,
  text: string,
  iconType: typeof SvgIcon,
}

const UNLINKED_PROFILE_ACTIONS: UnlinkedProfileAction[] = [
  {
    id: "claim",
    title: "Claim an existing profile",
    text: "Use the 9-digit code given to you by an agent.",
    iconType: HowToRegOutlined,
  },
  {
    id: "create",
    title: "Create a new profile",
    text: "Set up a new profile with your details.",
    iconType: PersonAddOutlined,
  },
];

function SetupProviderPageProfileView({onProfileActionClicked}: {
  onProfileActionClicked: (action: UnlinkedProfileAction) => void
}): ReactElement {
  return <StyledBoxColumn
    style={{display: "flex", flexDirection: "column", width: "84dvw", padding: PD_LG, maxWidth: PAGE_FRAGMENT_WIDTH}}>
    {UNLINKED_PROFILE_ACTIONS.map(action => <StyledListItem
      onClick={() => onProfileActionClicked(action)}
      title={action.title}
      text={action.text}
      textWrap={true}
      icon={action.iconType}/>)}
  </StyledBoxColumn>;
}

function ProviderPageProfileRolesView({selectedRoles, onConfirmClicked}: {
  selectedRoles?: ProviderPageProfileRole[],
  onConfirmClicked: (roles: ProviderPageProfileRole[]) => void
}): ReactElement {
  const [roles, setRoles] = React.useState<ProviderPageProfileRole[]>(selectedRoles || []);
  const supported = appTypeObject(PROVIDER_PAGE_PROFILE_SUPPORTED_ROLES);
  useEffect(() => {
    if (supported.length <= 1) {
      onConfirmClicked(supported);
    }
  }, []);
  return <StyledBoxColumn style={{width: "84dvw", padding: PD_MD, maxWidth: PAGE_FRAGMENT_WIDTH}}>
    <Typography variant={"h5"}>Setup Provider</Typography>
    <Typography variant={"body2"}>Tell us a bit about what kind of provider you are. Your choices do
      not affect how the page will appear publicly. You can always adjust these later. Select all that
      apply.</Typography>
    {supported.map(role => {
      const metadata = PROVIDER_PAGE_PROFILE_ROLES_KTM.findMetadata(role);
      return <StyledListItem
        selected={roles.includes(role)}
        onClick={() => {
          const index = roles.indexOf(role);
          if (index >= 0) {
            setRoles([...roles.slice(0, index), ...roles.slice(index + 1)]);
          } else {
            setRoles([...roles, role]);
          }
        }}
        title={metadata.text}
        text={metadata.description}
        textWrap={true}
        icon={metadata.iconType}/>;
    })}
    <StyledBoxRow>
      <StyledSpan/>
      <Button disabled={roles.length < 1} onClick={() => onConfirmClicked(roles)}>Confirm</Button>
    </StyledBoxRow>
  </StyledBoxColumn>;
}